"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { useFeedbackState } from "../ui/ToastProvider";
import { AUTH_SESSION_CHANGED_EVENT, clearToken, getToken } from "../../lib/auth";
import { isInvalidAdminSessionError } from "../../lib/sessionErrors";
import {
  getMe,
  getMeAccess,
  type AuthInviteProfile,
  type AuthInviteScope,
  type SupportSession,
} from "../../services/auth";
import { http } from "../../services/http";
import {
  getMyEstablishment,
  getSelectedEstablishmentIdFromStorage,
  listMyEstablishments,
  setSelectedEstablishmentIdInStorage,
  type MyEstablishment,
} from "../../services/establishments";

type MenuPermissions = {
  dashboard: boolean;
  units: boolean;
  personalization: boolean;
  availability: boolean;
  services: boolean;
  professionals: boolean;
  customFields: boolean;
  appointments: boolean;
  reviews: boolean;
  reports: boolean;
  customers: boolean;
  inventory: boolean;
  expenses: boolean;
  subscription: boolean;
  users: boolean;
  payments: boolean;
  customerSubscriptions: boolean;
  wallet: boolean;
  marketing: boolean;
  whatsapp: boolean;
  settings: boolean;
  activity: boolean;
};

type EffectiveAccessInfo = {
  establishmentId: string;
  ownerId: string;
  userId: string;
  role: "owner" | "admin" | "member";
  accessProfileCode: AuthInviteProfile;
  accessScope: AuthInviteScope;
  permissions: Record<string, boolean>;
  source: "owner" | "membership";
};

/** Sessão aberta pelo suporte, com o nome de quem a conta representa (ENTER-07.2). */
export type SupportSessionInfo = SupportSession & { personName: string };

function toSupportSession(
  me: {
    email: string;
    fullName: string | null;
    supportSession?: SupportSession | null;
  } | null
): SupportSessionInfo | null {
  const session = me?.supportSession;
  if (!me || !session || typeof session !== "object") return null;
  return {
    internalUserName: String(session.internalUserName || ""),
    mode: session.mode === "member" ? "member" : "owner",
    // Sem o campo (backend antigo), vale o mais restrito: só consulta.
    canWrite: session.canWrite === true,
    personName: me.fullName?.trim() || me.email,
  };
}

interface AppContextValue {
  est: MyEstablishment | null | undefined; // undefined = carregando, null = inexistente
  loadError: string | null;
  establishments: MyEstablishment[];
  selectedEstablishmentId: string | null;
  setSelectedEstablishmentId: (establishmentId: string) => void;
  role: "owner" | "admin" | "member" | null | undefined;
  isSuperAdmin: boolean | undefined;
  subscriptionActive?: boolean | null;
  subscriptionStatus?: string | null;
  subscriptionPlanExists?: boolean | null;
  refresh: () => Promise<void>;
  me:
    | { id: string; email: string; fullName?: string | null; cpf?: string | null }
    | null
    | undefined;
  effectiveAccess: EffectiveAccessInfo | null;
  menuPermissions: MenuPermissions | null;
  supportSession: SupportSessionInfo | null;
  data?: any;
}

const AppContext = createContext<AppContextValue | null>(null);
const EST_CACHE_KEY = "establishment-cache";
const EST_LIST_CACHE_KEY = "establishment-list-cache";
// Depois da primeira carga, uma recarga que falha tenta de novo em segundo
// plano, sem trocar a tela pelo cartão de erro (RET-05).
const BACKGROUND_RETRY_DELAYS_MS = [1000, 3000, 10000];

function loadCachedEst(): MyEstablishment | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(EST_CACHE_KEY);
    return raw ? (JSON.parse(raw) as MyEstablishment) : null;
  } catch {
    return null;
  }
}

function saveCachedEst(est: MyEstablishment | null) {
  if (typeof window === "undefined") return;
  try {
    if (est) {
      const serialized = JSON.stringify(est);
      window.sessionStorage.setItem(EST_CACHE_KEY, serialized);
    } else {
      window.sessionStorage.removeItem(EST_CACHE_KEY);
    }
  } catch {}
}

function loadCachedEstList(): MyEstablishment[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(EST_LIST_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MyEstablishment[]) : [];
  } catch {
    return [];
  }
}

function saveCachedEstList(list: MyEstablishment[]) {
  if (typeof window === "undefined") return;
  try {
    if (Array.isArray(list) && list.length > 0) {
      window.sessionStorage.setItem(EST_LIST_CACHE_KEY, JSON.stringify(list));
    } else {
      window.sessionStorage.removeItem(EST_LIST_CACHE_KEY);
    }
  } catch {}
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const isAdminRoute = Boolean(pathname?.startsWith("/admin"));
  const [est, setEst] = useState<MyEstablishment | null | undefined>(undefined);
  const [establishments, setEstablishments] = useState<MyEstablishment[]>([]);
  const [loadError, setLoadError] = useFeedbackState<string | null>(null);
  const [selectedEstablishmentId, setSelectedEstablishmentIdState] = useState<
    string | null
  >(null);
  const [role, setRole] = useState<AppContextValue["role"]>(undefined);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | undefined>(undefined);
  const [subscriptionActive, setSubscriptionActive] = useState<
    boolean | null | undefined
  >(undefined);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null | undefined>(
    undefined
  );
  const [subscriptionPlanExists, setSubscriptionPlanExists] = useState<
    boolean | null | undefined
  >(undefined);
  const [meUser, setMeUser] = useState<AppContextValue["me"]>(undefined);
  const [effectiveAccess, setEffectiveAccess] = useState<EffectiveAccessInfo | null>(
    null
  );
  const [menuPermissions, setMenuPermissions] = useState<MenuPermissions | null>(null);
  const [supportSession, setSupportSession] = useState<SupportSessionInfo | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const retryFiredRef = useRef(false);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const unmountedRef = useRef(false);

  const clearBackgroundRetry = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  }, []);

  const scheduleBackgroundRetry = useCallback(() => {
    clearBackgroundRetry();
    const delay = BACKGROUND_RETRY_DELAYS_MS[retryAttemptRef.current];
    if (delay === undefined || unmountedRef.current) return;
    retryAttemptRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      retryFiredRef.current = true;
      void refreshRef.current();
    }, delay);
  }, [clearBackgroundRetry]);

  const resetContext = useCallback(
    (loading = true) => {
      // Outra sessão (ou saída do admin): a próxima carga volta a ser a primeira.
      hasLoadedOnceRef.current = false;
      retryAttemptRef.current = 0;
      clearBackgroundRetry();
      setEst(loading ? undefined : null);
      setLoadError(null);
      setEstablishments([]);
      setSelectedEstablishmentIdState(null);
      setSelectedEstablishmentIdInStorage(null);
      setRole(loading ? undefined : null);
      setIsSuperAdmin(loading ? undefined : false);
      setMeUser(loading ? undefined : null);
      setEffectiveAccess(null);
      setMenuPermissions(null);
      setSupportSession(null);
      saveCachedEst(null);
      saveCachedEstList([]);
      setSubscriptionActive(loading ? undefined : null);
      setSubscriptionStatus(loading ? undefined : null);
      setSubscriptionPlanExists(loading ? undefined : null);
    },
    [clearBackgroundRetry]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(EST_CACHE_KEY);
    } catch {}
  }, []);

  const setSelectedEstablishmentId = useCallback(
    (establishmentId: string) => {
      const selected = establishments.find((item) => item.id === establishmentId);
      if (!selected) return;

      setSelectedEstablishmentIdState(establishmentId);
      setSelectedEstablishmentIdInStorage(establishmentId);
      setEst(selected);
      setRole(selected.membershipRole || null);
      saveCachedEst(selected);
    },
    [establishments]
  );

  const refresh = useCallback(async () => {
    // Recarga vinda de fora (evento online, botão, outra tela) recomeça a
    // sequência de tentativas; a disparada pelo timer continua de onde parou.
    const isBackgroundRetry = retryFiredRef.current;
    retryFiredRef.current = false;
    clearBackgroundRetry();
    if (!isBackgroundRetry) retryAttemptRef.current = 0;

    if (!isAdminRoute) {
      resetContext(false);
      return;
    }

    const token = getToken();
    if (!token) {
      resetContext(false);
      return;
    }
    try {
      setLoadError(null);
      // Carregar est do cache para render imediato
      const cachedEst = loadCachedEst();
      const cachedList = loadCachedEstList();
      setEst((prev) => {
        if (prev !== undefined) return prev;
        return cachedEst || prev;
      });
      setEstablishments((prev) => {
        if (prev.length > 0) return prev;
        return cachedList.length > 0 ? cachedList : prev;
      });

      const [meResult, establishmentsResult, fallbackResult] = await Promise.all([
        getMe(token).then(
          (value) => ({ ok: true as const, value }),
          (error) => ({ ok: false as const, error })
        ),
        listMyEstablishments(token).then(
          (value) => ({ ok: true as const, value }),
          (error) => ({ ok: false as const, error })
        ),
        getMyEstablishment(token).then(
          (value) => ({ ok: true as const, value }),
          (error) => ({ ok: false as const, error })
        ),
      ]);

      const fallbackNotFound =
        !fallbackResult.ok && Number(fallbackResult.error?.status) === 404;
      const hasUnauthorizedResponse = [
        meResult,
        establishmentsResult,
        fallbackResult,
      ].some((result) => !result.ok && isInvalidAdminSessionError(result.error));

      if (hasUnauthorizedResponse) {
        if (getToken() === token) {
          await clearToken();
        }
        resetContext(false);
        router.replace("/login");
        return;
      }

      const establishmentLoadFailed =
        !establishmentsResult.ok || (!fallbackResult.ok && !fallbackNotFound);

      if (establishmentLoadFailed && hasLoadedOnceRef.current) {
        // Mantém dados, faixa de suporte e tela atual; tenta de novo sozinho.
        scheduleBackgroundRetry();
        return;
      }

      // A faixa de suporte vale mesmo quando os estabelecimentos falham ao carregar.
      setSupportSession(toSupportSession(meResult.ok ? meResult.value : null));

      if (establishmentLoadFailed) {
        const hasCachedData = Boolean(cachedEst?.id || cachedList.length > 0);
        setLoadError(
          "Não conseguimos carregar seus dados agora. Pode ser uma instabilidade da nossa API."
        );
        if (!hasCachedData) {
          setRole((prev) => (prev === undefined ? null : prev));
          setMeUser(meResult.ok && meResult.value?.id ? meResult.value : null);
        }
        return;
      }

      const me = meResult.ok ? meResult.value : null;
      const establishmentsList = establishmentsResult.value;
      const fallbackEst = fallbackResult.ok ? fallbackResult.value : null;

      let list = Array.isArray(establishmentsList)
        ? establishmentsList.filter((item) => item && item.id)
        : [];
      if (list.length === 0 && fallbackEst?.id) {
        list = [fallbackEst];
      }

      const selectedFromStorage = getSelectedEstablishmentIdFromStorage();
      const selected =
        list.find((item) => item.id === selectedFromStorage) || list[0] || null;
      const subStatus = selected?.id
        ? await http<{
            status: string;
            canAccess: boolean;
            planExists?: boolean;
          }>("/subscriptions/me/status", {
            token,
            query: {
              establishmentId: selected.id,
            },
          }).catch(() => null as any)
        : null;
      const meAccess = selected?.id
        ? await getMeAccess(token, selected.id).catch(() => null)
        : null;

      let resolvedRole: "owner" | "admin" | "member" | null = null;
      if (meAccess?.access?.role) {
        resolvedRole = meAccess.access.role;
      } else if (selected?.membershipRole) {
        resolvedRole = selected.membershipRole;
      } else if (selected?.id) {
        const membership = await http<{ role: "owner" | "admin" | "member" }>(
          "/establishments/me/membership",
          {
            token,
            query: {
              establishmentId: selected.id,
            },
          }
        ).catch(() => null as any);
        resolvedRole = membership?.role || null;
      }

      const resolvedIsSuperAdmin =
        String(me?.email || "")
          .trim()
          .toLowerCase() === "root@mail.com";

      setIsSuperAdmin(resolvedIsSuperAdmin);
      if (me && me.email && me.id)
        setMeUser({ id: me.id, email: me.email, fullName: me.fullName, cpf: me.cpf });
      else setMeUser(null);
      setRole(resolvedRole || null);
      const resolvedAccess = meAccess?.access || null;
      setEffectiveAccess(
        resolvedAccess
          ? {
              establishmentId: resolvedAccess.establishmentId,
              ownerId: resolvedAccess.ownerId,
              userId: resolvedAccess.userId,
              role: resolvedAccess.role,
              accessProfileCode: resolvedAccess.accessProfileCode,
              accessScope: resolvedAccess.accessScope,
              permissions: resolvedAccess.permissions,
              source: resolvedAccess.source === "owner" ? "owner" : "membership",
            }
          : null
      );
      setMenuPermissions((meAccess?.menu as MenuPermissions | null) || null);
      setEstablishments(list);
      setSelectedEstablishmentIdState(selected?.id || null);
      setSelectedEstablishmentIdInStorage(selected?.id || null);
      setEst(selected);
      saveCachedEst(selected);
      saveCachedEstList(list);
      setLoadError(null);
      hasLoadedOnceRef.current = true;
      retryAttemptRef.current = 0;

      if (subStatus) {
        const isActive = subStatus.canAccess === true;

        setSubscriptionActive(isActive);
        setSubscriptionStatus(String(subStatus.status || ""));
        setSubscriptionPlanExists(
          subStatus.planExists === undefined ? null : Boolean(subStatus.planExists)
        );
      } else {
        setSubscriptionActive(null);
        setSubscriptionStatus(null);
        setSubscriptionPlanExists(null);
      }
    } catch {
      if (hasLoadedOnceRef.current) {
        scheduleBackgroundRetry();
        return;
      }
      setLoadError(
        "Não conseguimos carregar seus dados agora. Pode ser uma instabilidade da nossa API."
      );
      setRole((prev) => (prev === undefined ? null : prev));
    }
  }, [clearBackgroundRetry, isAdminRoute, resetContext, router, scheduleBackgroundRetry]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      clearBackgroundRetry();
    };
  }, [clearBackgroundRetry]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleAuthChange = () => {
      resetContext(Boolean(getToken()));
      void refresh();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === "auth_token") handleAuthChange();
    };

    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, handleAuthChange);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("online", refresh);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, handleAuthChange);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("online", refresh);
    };
  }, [refresh, resetContext]);

  const value = useMemo<AppContextValue>(
    () => ({
      est,
      loadError,
      establishments,
      selectedEstablishmentId,
      setSelectedEstablishmentId,
      role,
      isSuperAdmin,
      subscriptionActive,
      subscriptionStatus,
      subscriptionPlanExists,
      refresh,
      me: meUser,
      effectiveAccess,
      menuPermissions,
      supportSession,
    }),
    [
      est,
      loadError,
      establishments,
      selectedEstablishmentId,
      setSelectedEstablishmentId,
      role,
      isSuperAdmin,
      subscriptionActive,
      subscriptionStatus,
      subscriptionPlanExists,
      meUser,
      effectiveAccess,
      menuPermissions,
      supportSession,
      refresh,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within <AppProvider>");
  return ctx;
}

/** Versão segura de useApp — retorna null fora do AppProvider (ex.: testes isolados). */
export function useAppOptional() {
  return useContext(AppContext);
}
